import React, { useEffect, useState } from 'react';
import { Title, useRefresh } from 'react-admin';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import Chip from '@material-ui/core/Chip';
import Container from '@material-ui/core/Container';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined';
import CheckIcon from '@material-ui/icons/Check';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import EventOutlinedIcon from '@material-ui/icons/EventOutlined';
import FlagIcon from '@material-ui/icons/Flag';
import GestureIcon from '@material-ui/icons/Gesture';
import GroupAddIcon from '@material-ui/icons/GroupAdd';
import HistoryIcon from '@material-ui/icons/History';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import MenuBookOutlinedIcon from '@material-ui/icons/MenuBookOutlined';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import ScheduleIcon from '@material-ui/icons/Schedule';
import SearchIcon from '@material-ui/icons/Search';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import { useSnackbar } from 'notistack';

import AbasNavegador from '../../../Components/AbasNavegador';
import FiltrosTabela from '../../../Components/FiltrosTabela';
import TabelaDados, { AcoesLinha } from '../../../Components/TabelaDados';
import {
  BlocoDetalhe,
  Campo,
  DialogoDetalhes,
  formatarCompetencia,
  formatarDataHora,
  gradeCampos,
  legendaStatus,
  tipoDoBlob,
  urlDoArquivo,
  VisualizadorArquivo,
} from '../../../Resources/Documentos/List';
import { api } from '../../../services';
import { PRIMARY_COLOR } from '../../../utils';

// Mensagem do servidor ou a padrão. Com responseType 'blob' o corpo do erro
// (JSON) também chega como Blob.
async function mensagemDoErro(e, padrao) {
  try {
    const dados = e.response.data;
    const corpo =
      dados instanceof Blob ? JSON.parse(await dados.text()) : dados;
    return corpo.message || padrao;
  } catch (erro) {
    return padrao;
  }
}

// Localização do navegador no ato de assinar (evidência guardada no servidor, não
// exibida na tela). Nunca rejeita nem bloqueia a assinatura: sem coordenadas
// devolve o motivo.
function obterLocalizacao() {
  if (!navigator.geolocation) {
    return Promise.resolve({ motivo_sem_geolocalizacao: 'nao_suportada' });
  }
  const motivos = { 1: 'negada', 2: 'indisponivel', 3: 'tempo_esgotado' };
  const consulta = new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        resolve({
          latitude: coords.latitude,
          longitude: coords.longitude,
          precisao: coords.accuracy,
        }),
      (e) =>
        resolve({
          motivo_sem_geolocalizacao: motivos[e.code] || 'indisponivel',
        }),
      { timeout: 8000, maximumAge: 0, enableHighAccuracy: false },
    );
  });
  // O timeout do navegador não conta o tempo do aviso de permissão aberto.
  const limite = new Promise((resolve) => {
    setTimeout(
      () => resolve({ motivo_sem_geolocalizacao: 'tempo_esgotado' }),
      15000,
    );
  });
  return Promise.race([consulta, limite]);
}

// Cores dos status (fundo suave + texto escuro, contraste AA).
const statusChip = {
  pendente: { texto: 'Aguardando', fundo: '#fff4e5', cor: '#8a4600' },
  enviado: { texto: 'Arquivo enviado', fundo: '#e5f6fd', cor: '#01579b' },
  emitido: { texto: 'Só leitura', fundo: '#e8eaf6', cor: '#283593' },
  lido: { texto: 'Lido', fundo: '#e8f5e9', cor: '#1b5e20' },
  assinado: { texto: 'Assinado', fundo: '#e8f5e9', cor: '#1b5e20' },
  cancelado: { texto: 'Cancelado', fundo: '#ffebee', cor: '#b71c1c' },
  em_analise: { texto: 'Em análise', fundo: '#e5f6fd', cor: '#01579b' },
  rejeitado: { texto: 'Rejeitado', fundo: '#ffebee', cor: '#b71c1c' },
};

const StatusChip = ({ status, legenda = null }) => {
  const { texto, fundo, cor } = statusChip[status] || {
    texto: status,
    fundo: '#f5f5f5',
    cor: '#424242',
  };
  return (
    <>
      <Chip
        size="small"
        icon={
          status === 'assinado' ? (
            <VerifiedUserIcon style={{ color: cor, fontSize: 16 }} />
          ) : undefined
        }
        label={texto}
        style={{
          backgroundColor: fundo,
          color: cor,
          border: `1px solid ${cor}33`,
          fontWeight: 600,
        }}
      />
      {legenda && (
        <Typography variant="caption" display="block" color="textSecondary">
          {legenda}
        </Typography>
      )}
    </>
  );
};

const COR_ASSINAR = { cor: '#ed6c02', fundo: '#fff4e5' };
const COR_ENVIAR = { cor: '#0288d1', fundo: '#e5f6fd' };
const COR_LER = { cor: '#6a1b9a', fundo: '#f3e5f5' };

// Dia local (AAAA-MM-DD) de uma data, para comparar com os campos de data.
const diaLocal = (valor) => {
  const d = new Date(valor);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const dentroDoPeriodo = (valor, de, ate) => {
  if (!de && !ate) return true;
  if (!valor) return false;
  const dia = diaLocal(valor);
  return (!de || dia >= de) && (!ate || dia <= ate);
};

const FILTROS_HISTORICO_VAZIOS = {
  modelo_id: '',
  status: '',
  assinado_de: '',
  assinado_ate: '',
  criado_de: '',
  criado_ate: '',
};
const FILTROS_PENDENTES_VAZIOS = {
  modelo_id: '',
  tipo: '',
  criado_de: '',
  criado_ate: '',
};

const useStyles = makeStyles((theme) => ({
  pagina: {
    padding: theme.spacing(0, 2, 4),
    [theme.breakpoints.up('md')]: { padding: theme.spacing(0, 3, 4) },
  },
  // Cabeçalho no padrão da tela Documentos: título à esquerda, ação à direita.
  cabecalho: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 16,
    marginBottom: 16,
  },
  titulo: { fontWeight: 700, fontSize: '1.5rem' },
  botaoCriar: {
    borderRadius: 8,
    backgroundColor: '#2563eb',
    color: '#fff',
    textTransform: 'none',
    height: 40,
    padding: '0 16px',
    '&:hover': { backgroundColor: '#1d4ed8' },
  },
  vazio: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary,
  },
  barra: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    gap: theme.spacing(2),
    color: 'white',
    backgroundColor: PRIMARY_COLOR,
  },
  botao: { textTransform: 'none' },
}));

const Vazio = ({ icone, texto }) => {
  const classes = useStyles();
  return (
    <div className={classes.vazio}>
      {icone}
      <Typography variant="body2">{texto}</Typography>
    </div>
  );
};

// Tabela local no padrão novo das telas (TabelaDados): os dados já estão carregados, então
// a paginação acontece aqui no navegador.
const TabelaLista = ({ colunas, registros, vazio }) => {
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(25);
  useEffect(() => {
    setPagina(0);
  }, [registros.length]);
  return (
    <TabelaDados
      colunas={colunas}
      linhas={registros.slice(pagina * porPagina, (pagina + 1) * porPagina)}
      total={registros.length}
      pagina={pagina}
      porPagina={porPagina}
      onMudarPagina={setPagina}
      onMudarPorPagina={(quantidade) => {
        setPorPagina(quantidade);
        setPagina(0);
      }}
      rotuloItens="documentos"
      vazio={vazio}
    />
  );
};

// Indicador de etapas: círculos numerados ligados por uma linha; a etapa atual
// fica preenchida e as concluídas mostram um visto.
const IndicadorEtapas = ({ etapa, etapas }) => (
  <Box
    display="flex"
    alignItems="center"
    px={3}
    py={2}
    style={{ backgroundColor: '#f5f7fb', borderBottom: '1px solid #e3e7ef' }}
  >
    {etapas.map((rotulo, indice) => {
      const concluida = indice < etapa;
      const atual = indice === etapa;
      return (
        <React.Fragment key={rotulo}>
          {indice > 0 && (
            <div
              style={{
                flex: 1,
                height: 3,
                borderRadius: 2,
                margin: '0 12px',
                backgroundColor: concluida || atual ? PRIMARY_COLOR : '#d5dbe7',
              }}
            />
          )}
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 700,
                color: concluida || atual ? 'white' : '#7b8499',
                backgroundColor: concluida || atual ? PRIMARY_COLOR : '#e3e7ef',
                boxShadow: atual ? `0 0 0 4px ${PRIMARY_COLOR}33` : 'none',
              }}
            >
              {concluida ? <CheckIcon style={{ fontSize: 18 }} /> : indice + 1}
            </div>
            <Typography
              variant="body2"
              style={{
                fontWeight: atual ? 700 : 500,
                color: atual ? PRIMARY_COLOR : '#7b8499',
              }}
            >
              {rotulo}
            </Typography>
          </Box>
        </React.Fragment>
      );
    })}
  </Box>
);

// Cartão tracejado para escolher um PDF: nome, tamanho, visualizar, remover e trocar.
const SeletorPdf = ({ arquivo, onEscolher, onRemover, onVisualizar }) => {
  const classes = useStyles();
  return (
    <Paper
      variant="outlined"
      style={{
        padding: 16,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 16,
        borderStyle: 'dashed',
      }}
    >
      <PictureAsPdfIcon
        fontSize="large"
        color={arquivo ? 'primary' : 'disabled'}
      />
      <Box flex="1 1 160px" minWidth={0}>
        <Typography variant="subtitle2" noWrap>
          {arquivo ? arquivo.name : 'Nenhum arquivo selecionado'}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {arquivo
            ? `${(arquivo.size / 1024 / 1024).toFixed(2)} MB`
            : 'PDF ou imagem (JPG/PNG), até 10 MB'}
        </Typography>
      </Box>
      <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
        {arquivo && (
          <>
            <Button
              size="small"
              color="primary"
              className={classes.botao}
              startIcon={<VisibilityOutlinedIcon />}
              onClick={onVisualizar}
            >
              Visualizar
            </Button>
            <Button
              size="small"
              className={classes.botao}
              startIcon={<DeleteOutlineIcon />}
              onClick={onRemover}
            >
              Remover
            </Button>
          </>
        )}
        <Button
          size="small"
          color="primary"
          component="label"
          className={classes.botao}
          variant={arquivo ? 'outlined' : 'contained'}
          startIcon={<CloudUploadOutlinedIcon />}
        >
          {arquivo ? 'Trocar' : 'Selecionar arquivo'}
          <input
            hidden
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            onChange={(e) => {
              const [escolhido] = e.target.files;
              e.target.value = '';
              onEscolher(escolhido);
            }}
          />
        </Button>
      </Box>
    </Paper>
  );
};

const MinhasPendencias = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  // O refresh avisa o Layout para reavaliar o bloqueio por documentos pendentes.
  const refresh = useRefresh();
  const [documentos, setDocumentos] = useState([]);
  const [paraEnviar, setParaEnviar] = useState([]);
  const [paraLer, setParaLer] = useState([]);
  const [tiposParaEnviar, setTiposParaEnviar] = useState([]);
  const [novoEnvio, setNovoEnvio] = useState(null);
  const [buscaTipo, setBuscaTipo] = useState('');
  const [meusDocumentos, setMeusDocumentos] = useState([]);
  const [assinadosPorMim, setAssinadosPorMim] = useState([]);
  const [filtros, setFiltros] = useState(FILTROS_HISTORICO_VAZIOS);
  const [filtrosPend, setFiltrosPend] = useState(FILTROS_PENDENTES_VAZIOS);
  const [aba, setAba] = useState('pendentes');
  const [detalhes, setDetalhes] = useState(null);

  // Abrir o motivo de um envio rejeitado tira o item do contador do menu.
  function abrirDetalhesHistorico(r) {
    setDetalhes(r);
    if (r.status === 'rejeitado' && !r.rejeicao_vista_em) {
      api
        .put(`/documentos/${r.id}/rejeicao-vista`)
        .then(() => refresh())
        .catch(() => {});
    }
  }
  const [envio, setEnvio] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [salvando, setSalvando] = useState(false);

  async function carregar() {
    try {
      const response = await api.get('/documentos/meus');
      setDocumentos(response.data.data.paraAssinar);
      setParaEnviar(response.data.data.paraEnviar);
      setParaLer(response.data.data.paraLer || []);
      setTiposParaEnviar(response.data.data.tiposParaEnviar || []);
      setMeusDocumentos(response.data.data.meusDocumentos);
      setAssinadosPorMim(response.data.data.assinadosPorMim || []);
    } catch (e) {
      enqueueSnackbar(
        await mensagemDoErro(e, 'Erro ao carregar os documentos.'),
        {
          variant: 'error',
        },
      );
    }
  }

  useEffect(() => {
    carregar();
  }, []);

  async function handleAbrir(documento, assinar = true, rota = 'pdf-pessoal') {
    try {
      const response = await api.get(`/documentos/${documento.id}/${rota}`, {
        responseType: 'blob',
      });
      setPdf({
        id: documento.id,
        selo: documento.status === 'assinado' && rota === 'pdf-pessoal',
        titulo: documento.modelo && documento.modelo.nome,
        info: [
          ['Colaborador', documento.colaborador && documento.colaborador.nome],
          ['Competência', formatarCompetencia(documento.competencia)],
          ['Criado em', formatarDataHora(documento.created_at)],
          [
            'Assinante',
            documento.userAssinante &&
              ((documento.userAssinante.colaborador &&
                documento.userAssinante.colaborador.nome) ||
                documento.userAssinante.username),
          ],
        ],
        url: urlDoArquivo(response.data),
        tipo: tipoDoBlob(response.data),
        assinar,
        ler: documento.tipo === 'ler',
      });
    } catch (e) {
      enqueueSnackbar(await mensagemDoErro(e, 'Erro ao abrir o PDF.'), {
        variant: 'error',
      });
    }
  }

  function handleFechar() {
    URL.revokeObjectURL(pdf.url);
    setPdf(null);
  }

  // Ação principal da pendência, conforme o tipo (também usada pelo selo).
  function abrirPendencia(r) {
    if (r.tipo === 'assinar') handleAbrir(r);
    else if (r.tipo === 'ler') handleAbrir(r, false);
    else setEnvio({ documento: r, arquivo: null });
  }

  async function handleMarcarLido() {
    setSalvando(true);
    try {
      const response = await api.put(`/documentos/${pdf.id}/lido`);
      enqueueSnackbar(response.data.message, { variant: 'success' });
      handleFechar();
      carregar();
      refresh();
    } catch (e) {
      enqueueSnackbar(
        await mensagemDoErro(e, 'Erro ao marcar o documento como lido.'),
        { variant: 'error' },
      );
      // 409: o documento não está mais pendente; a lista precisa ser atualizada.
      if (e.response && e.response.status === 409) {
        handleFechar();
        carregar();
        refresh();
      }
    } finally {
      setSalvando(false);
    }
  }

  async function handleAssinar() {
    setSalvando(true);
    try {
      const localizacao = await obterLocalizacao();
      const response = await api.put(
        `/documentos/${pdf.id}/assinar`,
        localizacao,
      );
      enqueueSnackbar(response.data.message, { variant: 'success' });
      handleFechar();
      carregar();
      refresh();
    } catch (e) {
      enqueueSnackbar(await mensagemDoErro(e, 'Erro ao assinar o documento.'), {
        variant: 'error',
      });
      // 409: já assinado ou não mais pendente; a lista precisa ser atualizada.
      if (e.response && e.response.status === 409) {
        handleFechar();
        carregar();
        refresh();
      }
    } finally {
      setSalvando(false);
    }
  }

  async function handleEnviar() {
    setSalvando(true);
    try {
      const formData = new FormData();
      formData.append('file', envio.arquivo);
      const response = await api.post(
        `/documentos/${envio.documento.id}/arquivo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      enqueueSnackbar(response.data.message, { variant: 'success' });
      setEnvio(null);
      carregar();
      refresh();
    } catch (e) {
      enqueueSnackbar(await mensagemDoErro(e, 'Erro ao enviar o arquivo.'), {
        variant: 'error',
      });
    } finally {
      setSalvando(false);
    }
  }

  const comoAssinante = assinadosPorMim.filter((d) => d.papel !== 'testemunha');
  const comoTestemunha = assinadosPorMim.filter(
    (d) => d.papel === 'testemunha',
  );

  const nomeDoc = (d) => (d.modelo && d.modelo.nome) || 'documento';
  const modelosHistorico = Object.values(
    [...meusDocumentos, ...assinadosPorMim].reduce(
      (acc, d) => (d.modelo ? { ...acc, [d.modelo.id]: d.modelo } : acc),
      {},
    ),
  )
    .sort((x, y) => x.nome.localeCompare(y.nome))
    .map((m) => ({ id: String(m.id), name: m.nome }));
  const temFiltro = Object.values(filtros).some(Boolean);
  // Mesmos filtros nas abas Histórico, Como assinante e Como testemunha; "assinado em" é a
  // data da assinatura do próprio documento (histórico) ou a minha (nas dos outros).
  const filtrarHistorico = (lista) =>
    lista.filter(
      (d) =>
        (!filtros.modelo_id ||
          String(d.modelo && d.modelo.id) === String(filtros.modelo_id)) &&
        (!filtros.status || d.status === filtros.status) &&
        dentroDoPeriodo(
          d.minha_assinatura_em || (d.assinatura && d.assinatura.assinado_em),
          filtros.assinado_de,
          filtros.assinado_ate,
        ) &&
        dentroDoPeriodo(d.created_at, filtros.criado_de, filtros.criado_ate),
    );
  const meusFiltrados = filtrarHistorico(meusDocumentos);

  const pendentes = [
    ...documentos.map((d) => ({ ...d, tipo: 'assinar' })),
    ...paraEnviar
      .filter((d) => d.status === 'pendente')
      .map((d) => ({ ...d, tipo: 'enviar' })),
    ...paraLer.map((d) => ({ ...d, tipo: 'ler' })),
  ];
  const modelosPendentes = Object.values(
    pendentes.reduce(
      (acc, d) => (d.modelo ? { ...acc, [d.modelo.id]: d.modelo } : acc),
      {},
    ),
  )
    .sort((x, y) => x.nome.localeCompare(y.nome))
    .map((m) => ({ id: String(m.id), name: m.nome }));
  const temFiltroPend = Object.values(filtrosPend).some(Boolean);
  const pendentesFiltrados = pendentes.filter(
    (d) =>
      (!filtrosPend.modelo_id ||
        String(d.modelo && d.modelo.id) === String(filtrosPend.modelo_id)) &&
      (!filtrosPend.tipo || d.tipo === filtrosPend.tipo) &&
      dentroDoPeriodo(
        d.created_at,
        filtrosPend.criado_de,
        filtrosPend.criado_ate,
      ),
  );
  const opcoesModelo = (lista) =>
    lista.map(({ id, name }) => ({ id, nome: name }));
  const camposHistorico = [
    {
      chave: 'modelo_id',
      rotulo: 'Modelo',
      tipo: 'selecao',
      opcoes: opcoesModelo(modelosHistorico),
    },
    {
      chave: 'status',
      rotulo: 'Status',
      tipo: 'selecao',
      opcoes: Object.keys(statusChip).map((id) => ({
        id,
        nome: statusChip[id].texto,
      })),
    },
    { chave: 'assinado_de', rotulo: 'Assinado a partir de', tipo: 'data' },
    { chave: 'assinado_ate', rotulo: 'Assinado até', tipo: 'data' },
    { chave: 'criado_de', rotulo: 'Criado a partir de', tipo: 'data' },
    { chave: 'criado_ate', rotulo: 'Criado até', tipo: 'data' },
  ];
  const camposPendentes = [
    {
      chave: 'modelo_id',
      rotulo: 'Modelo',
      tipo: 'selecao',
      opcoes: opcoesModelo(modelosPendentes),
    },
    {
      chave: 'tipo',
      rotulo: 'Pendência',
      tipo: 'selecao',
      opcoes: [
        { id: 'assinar', nome: 'Assinar' },
        { id: 'enviar', nome: 'Enviar arquivo' },
        { id: 'ler', nome: 'Ler documento' },
      ],
    },
    { chave: 'criado_de', rotulo: 'Criado a partir de', tipo: 'data' },
    { chave: 'criado_ate', rotulo: 'Criado até', tipo: 'data' },
  ];

  const totalPendentes =
    documentos.length +
    paraEnviar.filter((d) => d.status === 'pendente').length +
    paraLer.length;

  // Botão ao lado de "Adicionar filtro", como nas outras telas.
  const acoesToolbar =
    tiposParaEnviar.length > 0
      ? [
          {
            label: 'Enviar documento',
            icon: <CloudUploadOutlinedIcon />,
            onClick: () =>
              setNovoEnvio({
                modeloId: '',
                observacao: '',
                dataDocumento: '',
                arquivo: null,
              }),
          },
        ]
      : [];

  const tipoEscolhido = novoEnvio
    ? tiposParaEnviar.find((t) => String(t.id) === String(novoEnvio.modeloId))
    : null;
  const tiposFiltrados = tiposParaEnviar.filter((t) =>
    `${t.nome} ${t.descricao || ''}`
      .toLowerCase()
      .includes(buscaTipo.trim().toLowerCase()),
  );

  // Confere tipo (PDF ou imagem JPG/PNG) e tamanho do arquivo escolhido; avisa quando recusa.
  function pdfAceito(arquivo) {
    if (!arquivo) return false;
    if (
      !['application/pdf', 'image/jpeg', 'image/png'].includes(arquivo.type)
    ) {
      enqueueSnackbar('Selecione um PDF ou uma imagem JPG/PNG.', {
        variant: 'error',
      });
      return false;
    }
    if (arquivo.size > 10 * 1024 * 1024) {
      enqueueSnackbar('O arquivo deve ter no máximo 10 MB.', {
        variant: 'error',
      });
      return false;
    }
    return true;
  }

  function escolherArquivo(arquivo) {
    if (pdfAceito(arquivo)) setEnvio({ ...envio, arquivo });
  }

  function escolherArquivoNovo(arquivo) {
    if (pdfAceito(arquivo)) setNovoEnvio({ ...novoEnvio, arquivo });
  }

  function visualizarArquivoNovo() {
    const tipo = tiposParaEnviar.find(
      (t) => String(t.id) === String(novoEnvio.modeloId),
    );
    setPdf({
      id: null,
      url: URL.createObjectURL(novoEnvio.arquivo),
      tipo: novoEnvio.arquivo.type,
      titulo: tipo ? tipo.nome : 'Documento',
      info: [['Arquivo selecionado', novoEnvio.arquivo.name]],
      assinar: false,
    });
  }

  // Envio por iniciativa do colaborador: o documento nasce já como enviado.
  async function handleEnviarNovo() {
    setSalvando(true);
    try {
      const formData = new FormData();
      formData.append('modelo_documento_id', novoEnvio.modeloId);
      if (novoEnvio.observacao) {
        formData.append('observacao', novoEnvio.observacao);
      }
      if (novoEnvio.dataDocumento) {
        formData.append('data_documento', novoEnvio.dataDocumento);
      }
      formData.append('file', novoEnvio.arquivo);
      const response = await api.post(
        '/documentos/enviar-por-iniciativa',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      enqueueSnackbar(response.data.message, { variant: 'success' });
      setNovoEnvio(null);
      carregar();
      refresh();
    } catch (e) {
      enqueueSnackbar(await mensagemDoErro(e, 'Erro ao enviar o documento.'), {
        variant: 'error',
      });
    } finally {
      setSalvando(false);
    }
  }

  function visualizarArquivoEscolhido() {
    setPdf({
      id: null,
      url: URL.createObjectURL(envio.arquivo),
      tipo: envio.arquivo.type,
      titulo: envio.documento.modelo && envio.documento.modelo.nome,
      info: [
        [
          'Colaborador',
          envio.documento.colaborador && envio.documento.colaborador.nome,
        ],
        ['Competência', formatarCompetencia(envio.documento.competencia)],
        ['Arquivo selecionado', envio.arquivo.name],
      ],
      assinar: false,
    });
  }

  const colunaDocumento = (comTestemunha) => ({
    chave: 'documento',
    titulo: 'Documento',
    fixar: 'esquerda',
    largura: 260,
    render: (r) => (
      <>
        {nomeDoc(r)}
        {comTestemunha && r.papel === 'testemunha' && (
          <Typography variant="caption" display="block" color="textSecondary">
            {`Testemunha de ${(r.colaborador && r.colaborador.nome) || '—'}`}
          </Typography>
        )}
      </>
    ),
  });
  const colunaCriadoEm = {
    chave: 'criado_em',
    titulo: 'Criado em',
    largura: 160,
    render: (r) => formatarDataHora(r.created_at),
  };
  const colunaCompetencia = {
    chave: 'competencia',
    titulo: 'Competência',
    largura: 130,
    render: (r) => formatarCompetencia(r.competencia),
  };
  const colunaAcoes = (acoes, largura = 100) => ({
    chave: 'acoes',
    titulo: 'Ações',
    fixar: 'direita',
    largura,
    render: (r) => <AcoesLinha acoes={acoes} registro={r} maxVisiveis={3} />,
  });

  // Histórico (meus documentos) e as abas "Como assinante/testemunha" (de outros colaboradores).
  const colunasHistorico = (outros = false) => [
    colunaDocumento(false),
    colunaCompetencia,
    ...(outros
      ? [
          {
            chave: 'colaborador',
            titulo: 'Colaborador',
            largura: 220,
            render: (r) => (r.colaborador && r.colaborador.nome) || '—',
          },
          {
            chave: 'assinei_em',
            titulo: 'Assinei em',
            largura: 170,
            render: (r) =>
              r.minha_assinatura_em
                ? new Date(r.minha_assinatura_em).toLocaleString('pt-BR')
                : '—',
          },
        ]
      : [
          {
            chave: 'assinatura',
            titulo: 'Assinatura',
            largura: 220,
            render: (r) => {
              if (!r.userAssinante) return '—';
              return (
                <>
                  {(r.userAssinante.colaborador &&
                    r.userAssinante.colaborador.nome) ||
                    r.userAssinante.username}
                  {r.assinatura && (
                    <Typography
                      variant="caption"
                      display="block"
                      color="textSecondary"
                    >
                      {new Date(r.assinatura.assinado_em).toLocaleString(
                        'pt-BR',
                      )}
                    </Typography>
                  )}
                </>
              );
            },
          },
        ]),
    colunaCriadoEm,
    {
      chave: 'status',
      titulo: 'Status',
      largura: 150,
      render: (r) => (
        <StatusChip status={r.status} legenda={legendaStatus(r)} />
      ),
    },
    colunaAcoes([
      {
        title: 'Detalhes',
        icon: <VisibilityOutlinedIcon />,
        onClick: (r) => abrirDetalhesHistorico(r),
      },
    ]),
  ];

  const colunasPendentes = [
    colunaDocumento(true),
    colunaCompetencia,
    {
      chave: 'pendencia',
      titulo: 'Pendência',
      largura: 200,
      render: (r) => {
        const cor =
          { assinar: COR_ASSINAR, ler: COR_LER }[r.tipo] || COR_ENVIAR;
        const texto =
          {
            assinar:
              r.papel === 'testemunha' ? 'Assinar como testemunha' : 'Assinar',
            ler: 'Ler documento',
          }[r.tipo] || 'Enviar arquivo';
        return (
          <Chip
            size="small"
            label={texto}
            clickable
            onClick={() => abrirPendencia(r)}
            style={{
              backgroundColor: cor.fundo,
              color: cor.cor,
              fontWeight: 600,
            }}
          />
        );
      },
    },
    colunaCriadoEm,
    colunaAcoes(
      [
        {
          title: 'Detalhes',
          icon: <VisibilityOutlinedIcon />,
          onClick: (r) => setDetalhes(r),
        },
        {
          title: 'Ler e assinar',
          icon: <GestureIcon />,
          show: (r) => r.tipo === 'assinar',
          onClick: (r) => handleAbrir(r),
          propsButton: { color: 'primary' },
        },
        {
          title: 'Ler e marcar como lido',
          icon: <MenuBookOutlinedIcon />,
          show: (r) => r.tipo === 'ler',
          onClick: (r) => handleAbrir(r, false),
          propsButton: { color: 'primary' },
        },
        {
          title: 'Enviar arquivo',
          icon: <CloudUploadOutlinedIcon />,
          show: (r) => r.tipo === 'enviar',
          onClick: (r) => setEnvio({ documento: r, arquivo: null }),
          propsButton: { color: 'primary' },
        },
      ],
      110,
    ),
  ];

  const vazioLista = (texto) => (
    <Vazio icone={<DescriptionOutlinedIcon fontSize="large" />} texto={texto} />
  );

  return (
    <Container maxWidth="lg" className={classes.pagina}>
      <Title title="Minhas pendências" />
      <Box className={classes.cabecalho}>
        <Typography variant="h4" className={classes.titulo}>
          Minhas pendências
        </Typography>
        <Box display="flex" style={{ gap: 8 }}>
          {acoesToolbar.map((acao) => (
            <Button
              key={acao.label}
              variant="contained"
              size="small"
              className={classes.botaoCriar}
              startIcon={acao.icon}
              onClick={acao.onClick}
            >
              {acao.label}
            </Button>
          ))}
        </Box>
      </Box>

      <Box mb={2}>
        {aba !== 'pendentes' ? (
          <FiltrosTabela
            campos={camposHistorico}
            valores={filtros}
            onChange={setFiltros}
          />
        ) : (
          <FiltrosTabela
            campos={camposPendentes}
            valores={filtrosPend}
            onChange={setFiltrosPend}
          />
        )}
      </Box>

      <Card style={{ borderRadius: 12, overflow: 'visible' }}>
        <AbasNavegador
          valor={aba}
          onChange={setAba}
          abas={[
            {
              id: 'pendentes',
              name: 'Pendentes',
              cor: '#f57c00',
              icon: <ScheduleIcon fontSize="small" />,
              contador: totalPendentes,
            },
            {
              id: 'historico',
              name: 'Histórico',
              cor: '#1976d2',
              icon: <HistoryIcon fontSize="small" />,
              contador: meusDocumentos.length,
            },
            {
              id: 'como_assinante',
              name: 'Como assinante',
              cor: '#388e3c',
              icon: <GestureIcon fontSize="small" />,
              contador: comoAssinante.length,
            },
            {
              id: 'como_testemunha',
              name: 'Como testemunha',
              cor: '#6a1b9a',
              icon: <GroupAddIcon fontSize="small" />,
              contador: comoTestemunha.length,
            },
          ]}
        />
        <Box p={2}>
          {aba === 'pendentes' && (
            <TabelaLista
              colunas={colunasPendentes}
              registros={pendentesFiltrados}
              vazio={vazioLista(
                temFiltroPend
                  ? 'Nenhuma pendência com esses filtros'
                  : 'Você está em dia. Nenhuma pendência no momento.',
              )}
            />
          )}
          {aba === 'historico' && (
            <TabelaLista
              colunas={colunasHistorico()}
              registros={meusFiltrados}
              vazio={vazioLista(
                temFiltro
                  ? 'Nenhum documento com esses filtros'
                  : 'Nenhum documento',
              )}
            />
          )}
          {aba === 'como_assinante' && (
            <TabelaLista
              colunas={colunasHistorico(true)}
              registros={filtrarHistorico(comoAssinante)}
              vazio={vazioLista(
                temFiltro
                  ? 'Nenhum documento com esses filtros'
                  : 'Você ainda não assinou documentos como assinante',
              )}
            />
          )}
          {aba === 'como_testemunha' && (
            <TabelaLista
              colunas={colunasHistorico(true)}
              registros={filtrarHistorico(comoTestemunha)}
              vazio={vazioLista(
                temFiltro
                  ? 'Nenhum documento com esses filtros'
                  : 'Você ainda não assinou documentos como testemunha',
              )}
            />
          )}
        </Box>
      </Card>

      <DialogoDetalhes
        documento={detalhes}
        onClose={() => setDetalhes(null)}
        onVisualizarPdf={(r) => handleAbrir(r, false)}
        onVisualizarArquivo={(r) => handleAbrir(r, false, 'arquivo-enviado')}
        comTrilha={false}
      />

      <Dialog
        open={Boolean(novoEnvio)}
        onClose={() => !salvando && setNovoEnvio(null)}
        fullWidth
        maxWidth="sm"
      >
        <div className={classes.barra}>
          <Typography variant="subtitle1" style={{ fontWeight: 600, flex: 1 }}>
            Enviar documento
          </Typography>
          <IconButton
            size="small"
            style={{ color: 'white' }}
            disabled={salvando}
            onClick={() => setNovoEnvio(null)}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <IndicadorEtapas
          etapa={tipoEscolhido ? 1 : 0}
          etapas={['Escolher o documento', 'Enviar o arquivo']}
        />
        <DialogContent>
          {novoEnvio && !tipoEscolhido && (
            <>
              <Typography variant="body2" gutterBottom>
                Escolha o documento que você quer enviar.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Pesquisar por nome ou instrução..."
                value={buscaTipo}
                onChange={(e) => setBuscaTipo(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Box
                mt={2}
                display="flex"
                flexDirection="column"
                style={{ gap: 8 }}
              >
                {tiposFiltrados.length === 0 && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    align="center"
                  >
                    Nenhum tipo de documento encontrado.
                  </Typography>
                )}
                {tiposFiltrados.map((tipo) => (
                  <Paper
                    key={tipo.id}
                    variant="outlined"
                    onClick={() => {
                      setNovoEnvio({ ...novoEnvio, modeloId: tipo.id });
                      setBuscaTipo('');
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: 12,
                      cursor: 'pointer',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        flexShrink: 0,
                        backgroundColor: COR_ENVIAR.fundo,
                        color: COR_ENVIAR.cor,
                      }}
                    >
                      <DescriptionOutlinedIcon />
                    </div>
                    <Box flex={1} minWidth={0}>
                      <Typography
                        variant="subtitle1"
                        style={{ fontWeight: 600 }}
                      >
                        {tipo.nome}
                      </Typography>
                      {tipo.descricao && (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {tipo.descricao}
                        </Typography>
                      )}
                    </Box>
                    <ChevronRightIcon color="action" />
                  </Paper>
                ))}
              </Box>
            </>
          )}

          {novoEnvio && tipoEscolhido && (
            <>
              <Box display="flex" alignItems="center" style={{ gap: 16 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    flexShrink: 0,
                    backgroundColor: COR_ENVIAR.cor,
                    color: 'white',
                  }}
                >
                  <DescriptionOutlinedIcon fontSize="large" />
                </div>
                <Box minWidth={0}>
                  <Typography
                    variant="overline"
                    color="textSecondary"
                    style={{ lineHeight: 1.5 }}
                  >
                    Documento a enviar
                  </Typography>
                  <Typography
                    variant="h5"
                    component="h2"
                    style={{ fontWeight: 700, wordBreak: 'break-word' }}
                  >
                    {tipoEscolhido.nome}
                  </Typography>
                </Box>
              </Box>
              {tipoEscolhido.descricao && (
                <Box
                  mt={2}
                  p={2}
                  borderRadius={8}
                  style={{
                    backgroundColor: COR_ENVIAR.fundo,
                    borderLeft: `4px solid ${COR_ENVIAR.cor}`,
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    mb={0.5}
                    style={{ gap: 6, color: COR_ENVIAR.cor }}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                    <Typography variant="subtitle2">Instruções</Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    style={{ whiteSpace: 'pre-wrap' }}
                  >
                    {tipoEscolhido.descricao}
                  </Typography>
                </Box>
              )}
              <BlocoDetalhe titulo="Arquivo">
                <SeletorPdf
                  arquivo={novoEnvio.arquivo}
                  onEscolher={escolherArquivoNovo}
                  onRemover={() =>
                    setNovoEnvio({ ...novoEnvio, arquivo: null })
                  }
                  onVisualizar={visualizarArquivoNovo}
                />
              </BlocoDetalhe>
              <BlocoDetalhe titulo="Informações (opcionais)">
                <TextField
                  type="date"
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Data do documento"
                  value={novoEnvio.dataDocumento}
                  onChange={(e) =>
                    setNovoEnvio({
                      ...novoEnvio,
                      dataDocumento: e.target.value,
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <Box mt={2}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    variant="outlined"
                    size="small"
                    label="Observação"
                    inputProps={{ maxLength: 500 }}
                    value={novoEnvio.observacao}
                    onChange={(e) =>
                      setNovoEnvio({ ...novoEnvio, observacao: e.target.value })
                    }
                  />
                </Box>
              </BlocoDetalhe>
            </>
          )}
        </DialogContent>
        {tipoEscolhido && (
          <DialogActions
            style={{
              justifyContent: 'space-between',
              padding: '12px 24px',
              borderTop: '1px solid #e3e7ef',
              backgroundColor: '#fafbff',
            }}
          >
            <Button
              color="primary"
              variant="outlined"
              className={classes.botao}
              startIcon={<ArrowBackIcon />}
              disabled={salvando}
              onClick={() =>
                setNovoEnvio({ ...novoEnvio, modeloId: '', arquivo: null })
              }
            >
              Voltar
            </Button>
            <Button
              color="primary"
              variant="contained"
              className={classes.botao}
              startIcon={<CloudUploadOutlinedIcon />}
              disabled={!novoEnvio.arquivo || salvando}
              onClick={handleEnviarNovo}
            >
              {salvando ? 'Enviando...' : 'Enviar'}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <Dialog
        open={Boolean(envio)}
        onClose={() => !salvando && setEnvio(null)}
        fullWidth
        maxWidth="sm"
      >
        <div className={classes.barra}>
          <Typography variant="subtitle1" style={{ fontWeight: 600, flex: 1 }}>
            Enviar arquivo
          </Typography>
          <IconButton
            size="small"
            style={{ color: 'white' }}
            disabled={salvando}
            onClick={() => setEnvio(null)}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent>
          {envio && (
            <>
              <BlocoDetalhe titulo="Documento">
                <Box style={gradeCampos}>
                  <Campo
                    icone={<DescriptionOutlinedIcon fontSize="small" />}
                    rotulo="Documento"
                  >
                    {envio.documento.modelo && envio.documento.modelo.nome}
                  </Campo>
                  <Campo
                    icone={<FlagIcon fontSize="small" />}
                    rotulo="Situação"
                  >
                    <Chip
                      size="small"
                      label={
                        envio.documento.motivo_rejeicao
                          ? 'Arquivo rejeitado, envie outro'
                          : 'Aguardando seu arquivo'
                      }
                      style={{
                        backgroundColor: COR_ENVIAR.fundo,
                        color: COR_ENVIAR.cor,
                        fontWeight: 600,
                      }}
                    />
                  </Campo>
                  <Campo
                    icone={<CalendarTodayOutlinedIcon fontSize="small" />}
                    rotulo="Competência"
                  >
                    {formatarCompetencia(envio.documento.competencia)}
                  </Campo>
                  <Campo
                    icone={<EventOutlinedIcon fontSize="small" />}
                    rotulo="Solicitado em"
                  >
                    {formatarDataHora(envio.documento.created_at)}
                  </Campo>
                  {envio.documento.motivo_rejeicao && (
                    <Campo
                      icone={<InfoOutlinedIcon fontSize="small" />}
                      rotulo="Motivo da rejeição"
                    >
                      {envio.documento.motivo_rejeicao}
                    </Campo>
                  )}
                </Box>
                {envio.documento.modelo && envio.documento.modelo.descricao && (
                  <Box mt={2}>
                    <Divider />
                    <Box mt={2}>
                      <Campo
                        icone={<InfoOutlinedIcon fontSize="small" />}
                        rotulo="Instruções"
                      >
                        <span style={{ whiteSpace: 'pre-wrap' }}>
                          {envio.documento.modelo.descricao}
                        </span>
                      </Campo>
                    </Box>
                  </Box>
                )}
              </BlocoDetalhe>
              <BlocoDetalhe titulo="Arquivo">
                <SeletorPdf
                  arquivo={envio.arquivo}
                  onEscolher={escolherArquivo}
                  onRemover={() => setEnvio({ ...envio, arquivo: null })}
                  onVisualizar={visualizarArquivoEscolhido}
                />
              </BlocoDetalhe>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            className={classes.botao}
            startIcon={<CloudUploadOutlinedIcon />}
            disabled={!envio || !envio.arquivo || salvando}
            onClick={handleEnviar}
          >
            {salvando ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pdf)} onClose={handleFechar} fullScreen>
        <div className={classes.barra}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" noWrap style={{ fontWeight: 600 }}>
              {(pdf && pdf.titulo) || 'Documento'}
            </Typography>
            {pdf && pdf.info && (
              <Box display="flex" flexWrap="wrap" style={{ columnGap: 24 }}>
                {pdf.info
                  .filter(([, valor]) => valor)
                  .map(([rotulo, valor]) => (
                    <Typography key={rotulo} variant="caption">
                      <span style={{ opacity: 0.8 }}>{rotulo}: </span>
                      <strong>{valor}</strong>
                    </Typography>
                  ))}
              </Box>
            )}
          </div>
          {pdf && pdf.selo && (
            <Chip
              icon={<VerifiedUserIcon style={{ color: '#1b5e20' }} />}
              label="Documento assinado"
              style={{
                backgroundColor: '#e8f5e9',
                color: '#1b5e20',
                fontWeight: 600,
              }}
            />
          )}
          <IconButton
            size="small"
            style={{ color: 'white' }}
            disabled={salvando}
            onClick={handleFechar}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <DialogContent style={{ padding: 0, display: 'flex' }}>
          {pdf && <VisualizadorArquivo url={pdf.url} tipo={pdf.tipo} />}
        </DialogContent>
        {pdf && (pdf.assinar || pdf.ler) && (
          <DialogActions
            style={{
              padding: '12px 24px',
              borderTop: '1px solid #e3e7ef',
              backgroundColor: '#fafbff',
            }}
          >
            <Button
              color="primary"
              variant="contained"
              className={classes.botao}
              startIcon={pdf.ler ? <DoneAllIcon /> : <GestureIcon />}
              disabled={salvando}
              onClick={pdf.ler ? handleMarcarLido : handleAssinar}
            >
              {pdf.ler ? 'Marcar como lido' : 'Assinar'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Container>
  );
};

export default MinhasPendencias;
